import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Target } from 'lucide-react';
import { API } from '../App';

// Budgets tab (Phase 2). Combines the existing EOM projection (/cost-projection)
// with a per-cluster budget target (GET/PUT /api/v1/clusters/{id}/budget) to show
// budget-vs-actual + forecast + an overage indicator. Degrades gracefully:
// no projection → hint; no budget → set-a-budget form/CTA.

const num = (v, d = 2) => (typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : '—');

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const card = (label, value, sub, accent) => (
  <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
    <CardContent className="p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary mb-2">{label}</p>
      <p className={`text-3xl font-semibold tabular-nums ${accent || 'text-slate-900 dark:text-white'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

export default function BudgetsPanel({ clusterId, projection, budget, onSaved }) {
  const proj = projection || {};
  const monthlyBudget = budget?.monthly_budget_usd;
  const threshold = budget?.alert_threshold_pct ?? 90;
  const forecast = proj.projected_monthly_cost;

  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(monthlyBudget ? String(monthlyBudget) : '');
  const [thresh, setThresh] = useState(String(threshold));
  const [saving, setSaving] = useState(false);

  const overPct = monthlyBudget && forecast ? (forecast / monthlyBudget) * 100 : null;
  const accent = overPct == null ? '' : overPct > 100 ? 'text-rose-600 dark:text-rose-400' : overPct > threshold ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
  const statusLabel = overPct == null ? null : overPct > 100 ? 'Over budget' : overPct > threshold ? 'Approaching budget' : 'On track';

  const breakdown = proj.breakdown_by_namespace || {};
  const nsRows = Object.entries(breakdown)
    .map(([ns, v]) => ({ ns, cost: typeof v === 'number' ? v : (v?.projected_monthly_cost ?? v?.cost ?? 0) }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 12);

  const save = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('Enter a monthly budget greater than 0'); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/v1/clusters/${clusterId}/budget`,
        { monthly_budget_usd: val, alert_threshold_pct: parseFloat(thresh) || 90 },
        { headers: authHeaders() }
      );
      toast.success('Budget saved');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(`Failed to save budget: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {projection ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {card('Spent (month-to-date)', `$${num(proj.actual_cost_to_date)}`, `${num(proj.completion_percentage, 0)}% of month elapsed`)}
          {card('Forecast (end of month)', `$${num(forecast)}`, `$${num(proj.current_daily_average)}/day current run-rate`, accent)}
          {monthlyBudget
            ? card('Monthly Budget', `$${num(monthlyBudget)}`, statusLabel ? `${statusLabel} · ${num(overPct, 0)}% of budget` : null, accent)
            : card('Monthly Budget', '—', 'not set')}
          {card('Days Elapsed', `${proj.days_elapsed ?? '—'} / ${proj.total_days_in_month ?? '—'}`, `${proj.month ?? ''} ${proj.year ?? ''}`)}
        </div>
      ) : (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardContent className="py-10 text-center text-sm text-slate-500 dark:text-text-secondary">
            Cost projection not available yet for this cluster.
          </CardContent>
        </Card>
      )}

      {/* Budget vs forecast bar */}
      {monthlyBudget && forecast != null && (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Forecast vs Budget</h3>
              <span className={`text-sm font-semibold ${accent}`}>{statusLabel} · {num(overPct, 0)}%</span>
            </div>
            <div className="w-full h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className={`h-full ${overPct > 100 ? 'bg-rose-500' : overPct > threshold ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, overPct)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-500 dark:text-text-secondary mt-1">
              <span>${num(forecast)} forecast</span>
              <span>${num(monthlyBudget)} budget</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Set / update budget */}
      <Card className={`dark:bg-surface-dark dark:border-surface-border ${!monthlyBudget ? 'border-dashed' : ''}`}>
        <CardContent className="p-5">
          {(!monthlyBudget && !editing) ? (
            <div className="text-center">
              <Target className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">Set a monthly budget</h3>
              <p className="text-slate-600 dark:text-text-secondary text-sm max-w-md mx-auto mb-4">
                Track actual spend and the end-of-month forecast against a target, with an overage indicator when you're
                trending over.
              </p>
              <button onClick={() => setEditing(true)}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500">Set budget</button>
            </div>
          ) : (editing ? (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Monthly budget (USD)</label>
                <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="w-40 border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-900" placeholder="e.g. 5000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Warn at (% of budget)</label>
                <input type="number" min="0" max="200" value={thresh} onChange={(e) => setThresh(e.target.value)}
                  className="w-32 border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-900" placeholder="90" />
              </div>
              <button onClick={save} disabled={saving}
                className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} disabled={saving}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-700">
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600 dark:text-text-secondary">
                Monthly budget <span className="font-semibold text-slate-900 dark:text-white">${num(monthlyBudget)}</span> · warn at {num(threshold, 0)}%
              </div>
              <button onClick={() => { setAmount(String(monthlyBudget)); setThresh(String(threshold)); setEditing(true); }}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-700">Edit</button>
            </div>
          ))}
        </CardContent>
      </Card>

      {nsRows.length > 0 && (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Projected Cost by Namespace</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-text-secondary border-b border-slate-200 dark:border-surface-border">
                    <th className="py-2 pr-4">Namespace</th>
                    <th className="py-2 text-right">Projected $/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {nsRows.map((r) => (
                    <tr key={r.ns} className="border-b border-slate-100 dark:border-surface-border/50">
                      <td className="py-2 pr-4 font-medium text-slate-900 dark:text-white">{r.ns}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700 dark:text-slate-300">${num(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
